import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { EmptyState } from "../../components/customer-app/ui";
import { ChevronLeft, FileText, CheckCircle2, XCircle, Loader2, Route, Hourglass, ScrollText } from "lucide-react";

const STATUS_META = {
  draft: { label: "Draft", tone: "bg-slate-100 text-slate-600" },
  sent: { label: "Awaiting Your Decision", tone: "bg-amber-100 text-amber-700" },
  customer_evaluation_wip: { label: "Under Your Review", tone: "bg-amber-100 text-amber-700" },
  verbally_confirmed_accepted: { label: "Verbally Confirmed", tone: "bg-emerald-100 text-emerald-700" },
  reprocessing_required: { label: "Being Revised", tone: "bg-orange-100 text-orange-700" },
  accepted: { label: "Accepted", tone: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Declined", tone: "bg-rose-100 text-rose-700" },
};

function money(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export default function CustomerQuotation() {
  const { oid, lid } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [deciding, setDeciding] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(
        lid ? `/customer-app/leads/${lid}/quotation` : `/customer-app/opportunities/${oid}/quotation`
      );
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [oid, lid]); // eslint-disable-line react-hooks/exhaustive-deps

  async function decide(decision) {
    const verb = decision === "accept" ? "accept" : "decline";
    if (!window.confirm(`Are you sure you want to ${verb} this quotation? This cannot be undone.`)) return;
    setDeciding(true);
    try {
      await api.post(`/customer-app/quotations/${data.id}/decision`, { decision });
      toast.success(decision === "accept" ? "Quotation accepted!" : "Quotation declined.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setDeciding(false); }
  }

  if (loading) {
    return <div className="pt-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0D4721]" /></div>;
  }

  if (!data?.available) {
    return (
      <div className="pb-4" data-testid="customer-quotation-unavailable">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 mb-3">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </button>
        <EmptyState
          icon={Hourglass}
          title={data?.reason === "no_quote" ? "Quotation not submitted yet" : "Not ready yet"}
          subtitle="Once our team submits your quotation, you'll be able to review and approve it right here."
        />
      </div>
    );
  }

  const meta = STATUS_META[data.status] || STATUS_META.draft;
  const decidable = ["sent", "customer_evaluation_wip", "reprocessing_required", "verbally_confirmed_accepted"].includes(data.status);
  const decided = data.status === "accepted" || data.status === "rejected";

  return (
    <div className="pb-6 -mx-5" data-testid="customer-quotation-page">
      <div className="px-5">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 mb-3">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </button>
      </div>

      {/* Distinct "document" theme — deep charcoal + gold, separate from the app's cream/emerald look */}
      <div className="bg-[#151312] mx-5 rounded-3xl overflow-hidden shadow-[0_12px_40px_-8px_rgba(0,0,0,0.35)]">
        <div className="px-6 pt-6 pb-8 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-amber-500/10 blur-3xl" />
          <div className="flex items-center gap-2 text-amber-400/90">
            <ScrollText className="w-4 h-4" />
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold">Official Quotation</span>
          </div>
          <div className="mt-2 font-mono text-xs text-white/50">{data.quotation_number} · {data.opportunity_code}</div>
          <div className="mt-1 text-lg font-bold text-white/90">{data.opportunity_name}</div>
          <span className={"inline-block mt-3 text-[11px] font-bold px-2.5 py-1 rounded-full " + meta.tone} data-testid="quotation-status-pill">
            {meta.label}
          </span>
        </div>

        {/* Line items */}
        <div className="bg-[#FAF8F5] px-5 py-5">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">Materials Considered</div>
          <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
                  <th className="text-left px-3 py-2 font-semibold">Material</th>
                  <th className="text-center px-2 py-2 font-semibold">Qty</th>
                  <th className="text-right px-2 py-2 font-semibold">Rate</th>
                  <th className="text-right px-3 py-2 font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(data.lines || []).map((l, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`quotation-line-${i}`}>
                    <td className="px-3 py-2.5 font-semibold text-slate-800">{l.material_name}</td>
                    <td className="px-2 py-2.5 text-center font-mono text-slate-600">{l.quantity}</td>
                    <td className="px-2 py-2.5 text-right font-mono text-slate-600">{money(l.unit_price)}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-semibold text-slate-800">{money(l.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mt-4 space-y-1.5">
            <div className="flex justify-between text-sm text-slate-500">
              <span>Subtotal</span><span className="font-mono">{money(data.subtotal + (data.discount || 0))}</span>
            </div>
            {data.discount > 0 && (
              <div className="flex justify-between text-sm text-emerald-600">
                <span>Discount</span><span className="font-mono">− {money(data.discount)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm text-slate-500">
              <span>Tax ({data.tax_percent}%)</span><span className="font-mono">{money(data.tax_amount)}</span>
            </div>
          </div>

          {/* Total Value — eye-catching */}
          <div className="mt-5 pt-5 border-t-2 border-dashed border-amber-300 flex items-end justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold">Total Value</div>
              <div
                className="text-4xl sm:text-5xl font-black tracking-tight mt-1 bg-gradient-to-br from-amber-600 via-amber-500 to-emerald-700 bg-clip-text text-transparent"
                style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
                data-testid="quotation-grand-total"
              >
                {money(data.grand_total)}
              </div>
            </div>
            <FileText className="w-8 h-8 text-amber-400/40 flex-shrink-0 mb-1" />
          </div>

          {data.notes && (
            <div className="mt-4 text-xs text-slate-500 bg-slate-50 rounded-xl p-3">{data.notes}</div>
          )}
        </div>
      </div>

      {/* Decision */}
      <div className="px-5 mt-5">
        {decided ? (
          <div className={"rounded-2xl p-4 flex items-center gap-3 " + (data.status === "accepted" ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200")} data-testid="quotation-decided-banner">
            {data.status === "accepted" ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" /> : <XCircle className="w-5 h-5 text-rose-600 flex-shrink-0" />}
            <div className={"text-sm font-semibold " + (data.status === "accepted" ? "text-emerald-800" : "text-rose-800")}>
              You {data.status === "accepted" ? "accepted" : "declined"} this quotation.
            </div>
          </div>
        ) : decidable ? (
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => decide("decline")} disabled={deciding} data-testid="quotation-decline-btn"
              className="py-3.5 rounded-xl border-2 border-rose-200 text-rose-600 font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-40">
              <XCircle className="w-4 h-4" /> Decline
            </button>
            <button onClick={() => decide("accept")} disabled={deciding} data-testid="quotation-accept-btn"
              className="py-3.5 rounded-xl bg-gradient-to-br from-emerald-600 to-[#0D4721] text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-40">
              {deciding ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Accept
            </button>
          </div>
        ) : null}

        <Link to={`/app/track/${oid}`} data-testid="quotation-track-progress-link"
          className="mt-4 w-full flex items-center justify-center gap-2 border-2 border-slate-200 rounded-xl py-3 text-sm font-bold text-slate-700 hover:border-[#0D4721] hover:text-[#0D4721] transition-colors">
          <Route className="w-4 h-4" /> Track Full Progress
        </Link>
      </div>
    </div>
  );
}
